import { useEffect, useRef, useState } from "react";
import { useTopLayerMenu } from "./useTopLayerMenu";

/** What a week's `⋯` offers. `docs/PROGRAMS-DESIGN.md` §4. */
export type WeekAction = "duplicate" | "remove" | "auto";

/**
 * A WEEK'S `⋯` ON THE PROGRAM BOARD — `Duplicate week` and `Remove week`.
 *
 * ⚠️ ITS OWN COMPONENT AND ITS OWN CLASS NAMES, NOT `AssignmentMenu` REUSED, AND NOT
 * `assignment-menu` BORROWED. That component's items come from `menuItems(assignment)` — it is
 * the DAY CARD's menu and its vocabulary is hide/show/remove an assignment; passing a week to it
 * would mean either a second item source or a prop that means "ignore everything you are". And
 * borrowing its class prefix would put the word `assignment` on a control that touches no
 * assignment at all: a class name is a vocabulary.
 *
 * ⚠️ WHAT IS SHARED IS THE STYLING, ONCE, AS A SELECTOR LIST IN `app.css` — the declarations are
 * written in one place and two names point at them, rather than one set of rules copied into a
 * second block that drifts the first time either is touched.
 *
 * 🔴 KEYBOARD-REACHABLE, and that is the falsifiable half of "better than Everfit" here for the
 * same reason the day card's menu says so: a real `<button>`, Escape to close, focus returned to
 * the trigger, arrow keys through the items. Two week-destroying actions behind a mouse-only
 * affordance are two actions a keyboard coach cannot reach.
 *
 * ⚠️ AND IT IS A MENU, NOT A DIALOG. `Remove week` does not confirm HERE; it hands back to the
 * board, which owns the sentence about what a removal destroys. A confirm written inside a
 * popover would be a second copy of the one rule that must not understate it.
 */
export function WeekMenu({ week, canRemove, canAuto = false, onChoose }: {
  week: number;
  /** False for the last remaining week — a block with no weeks has nowhere to draw. */
  canRemove: boolean;
  /** F15 3.9 — a WRITTEN week after week 1 may be handed to the engine (`Make auto`); absent = not offered. */
  canAuto?: boolean;
  onChoose: (action: WeekAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const items: { kind: WeekAction; label: string }[] = [
    { kind: "duplicate", label: "Duplicate week" },
    ...(canAuto ? [{ kind: "auto" as const, label: "Make auto" }] : []),
    ...(canRemove ? [{ kind: "remove" as const, label: "Remove week" }] : []),
  ];

  /* W79 (Fable's add) — the SAME top-layer menu as the day cell's. MEASURED 2026-09-25 at 375: this list opened at
     x −81…95, off the screen's left edge, every item unreachable at its left. */
  useTopLayerMenu(open, trigger, list);

  useEffect(() => {
    if (!open) return;
    /* Focus lands IN the menu on open — otherwise a keyboard coach opens a menu they are not in. */
    list.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);

  function close(restoreFocus: boolean) {
    setOpen(false);
    setActive(0);
    if (restoreFocus) trigger.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); close(true); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown"
        ? (active + 1) % items.length
        : (active - 1 + items.length) % items.length;
      setActive(next);
      list.current?.querySelectorAll<HTMLButtonElement>("button")[next]?.focus();
    }
  }

  return (
    <div className="week-menu" onKeyDown={onKeyDown}>
      <button ref={trigger} type="button" className="week-menu-trigger"
              aria-haspopup="menu" aria-expanded={open}
              aria-label={`Actions for week ${String(week)}`}
              onClick={() => setOpen(!open)}>⋯</button>
      {open && (
        <ul ref={list} className="week-menu-list is-top-layer" role="menu"
            aria-label={`Actions for week ${String(week)}`}>
          {items.map((item) => (
            <li key={item.kind} role="none">
              <button type="button" role="menuitem"
                      className={item.kind === "remove"
                        ? "week-menu-item is-destructive" : "week-menu-item"}
                      onClick={() => { close(false); onChoose(item.kind); }}>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* A click outside closes it WITHOUT restoring focus — the coach has already moved their
          attention elsewhere, and yanking the caret back is the focus steal this repo has fixed
          before. */}
      {open && <div className="week-menu-scrim" onClick={() => close(false)} aria-hidden="true" />}
    </div>
  );
}
